import type { CallFailureType, ToolFlag } from './voiceAgentOps.types'

/**
 * Readers for `CallData.data`: the ElevenLabs post-call payload, stored as received
 * under `transcriptWebhook` and typed only as JSON in GraphQL. Every reader tolerates
 * missing or oddly shaped fields, since nothing types the payload end to end.
 */

type JsonRecord = Record<string, unknown>

function asRecord(value: unknown): JsonRecord | undefined {
  if (!value || typeof value !== `object` || Array.isArray(value)) return undefined
  return value as JsonRecord
}

/** ElevenLabs sends tool params, tool results and some context values as JSON strings; unwrap them for display. */
export function parseJsonString(value: unknown): unknown {
  if (typeof value !== `string`) return value
  const trimmed = value.trim()
  if (!trimmed.startsWith(`{`) && !trimmed.startsWith(`[`)) return value
  try {
    return JSON.parse(trimmed)
    // oxlint-disable-next-line local/require-catch-logging -- not JSON after all; show the raw string
  } catch {
    return value
  }
}

function webhookOf(data: unknown): JsonRecord | undefined {
  return asRecord(asRecord(data)?.transcriptWebhook)
}

export type TranscriptToolCall = { toolName: string; params: unknown }
export type TranscriptToolResult = { toolName: string; result: unknown; isError: boolean }

export type TranscriptEntry = {
  role: string
  message?: string
  timeInCallSecs?: number
  toolCalls: TranscriptToolCall[]
  toolResults: TranscriptToolResult[]
}

export function readTranscript(data: unknown): TranscriptEntry[] {
  const transcript = webhookOf(data)?.transcript
  if (!Array.isArray(transcript)) return []
  return transcript.map((raw) => {
    const entry = asRecord(raw) ?? {}
    return {
      role: typeof entry.role === `string` ? entry.role : `unknown`,
      message: typeof entry.message === `string` && entry.message.trim() ? entry.message : undefined,
      timeInCallSecs: typeof entry.time_in_call_secs === `number` ? entry.time_in_call_secs : undefined,
      toolCalls: (Array.isArray(entry.tool_calls) ? entry.tool_calls : []).map((rawCall) => {
        const call = asRecord(rawCall) ?? {}
        return { toolName: String(call.tool_name ?? `tool`), params: parseJsonString(call.params_as_json) }
      }),
      toolResults: (Array.isArray(entry.tool_results) ? entry.tool_results : []).map((rawResult) => {
        const result = asRecord(rawResult) ?? {}
        return {
          toolName: String(result.tool_name ?? `tool`),
          result: parseJsonString(result.result_value),
          isError: result.is_error === true,
        }
      }),
    }
  })
}

/** One tool call the agent made, with the result ElevenLabs recorded for it. */
export type AgentToolCall = {
  toolName: string
  params: unknown
  timeInCallSecs?: number
  /** Undefined when the payload holds no result for the call. */
  result?: { value: unknown; isError: boolean }
}

/**
 * Every tool call in the transcript, in the order the agent made them. A result can
 * land on a later transcript entry than its call, so the two are paired on
 * ElevenLabs' `request_id`.
 */
export function readToolCalls(data: unknown): AgentToolCall[] {
  const transcript = webhookOf(data)?.transcript
  if (!Array.isArray(transcript)) return []
  const entries = transcript.map((raw) => asRecord(raw) ?? {})
  const listOf = (value: unknown) => (Array.isArray(value) ? value : []).map((raw) => asRecord(raw) ?? {})
  const resultByRequestId = new Map(
    entries
      .flatMap((entry) => listOf(entry.tool_results))
      .filter((result) => typeof result.request_id === `string`)
      .map((result) => [
        result.request_id as string,
        { value: parseJsonString(result.result_value), isError: result.is_error === true },
      ]),
  )
  return entries.flatMap((entry) =>
    listOf(entry.tool_calls).map((call) => ({
      toolName: String(call.tool_name ?? `tool`),
      params: parseJsonString(call.params_as_json),
      timeInCallSecs: typeof entry.time_in_call_secs === `number` ? entry.time_in_call_secs : undefined,
      result: typeof call.request_id === `string` ? resultByRequestId.get(call.request_id) : undefined,
    })),
  )
}

const TOOL_DIAGNOSTIC_KEYS: [ToolFlag, string][] = [
  [`toolError`, `tool_error`],
  [`toolTimeout`, `tool_timeout`],
  [`toolDuplicate`, `tool_duplicate`],
]

export type ToolDiagnosticResult = { flag: ToolFlag; value: unknown; rationale?: string }

/**
 * The tool_error / tool_timeout / tool_duplicate results. ElevenLabs nests data
 * collection under `analysis.data_collection_results.<id> = { value, rationale }`;
 * the backend's diagnostics check reads them flat on `analysis`, so accept both.
 */
export function readToolDiagnostics(data: unknown): ToolDiagnosticResult[] {
  const analysis = asRecord(webhookOf(data)?.analysis)
  const collected = asRecord(analysis?.data_collection_results)
  return TOOL_DIAGNOSTIC_KEYS.flatMap(([flag, key]) => {
    const nested = asRecord(collected?.[key])
    if (nested) {
      return [
        { flag, value: nested.value, rationale: typeof nested.rationale === `string` ? nested.rationale : undefined },
      ]
    }
    return analysis && key in analysis ? [{ flag, value: analysis[key] }] : []
  })
}

/** The dynamic variables passed to ElevenLabs when the call started (the agent's context), JSON strings unwrapped. */
export function readDynamicVariables(data: unknown): [string, unknown][] {
  const variables = asRecord(asRecord(webhookOf(data)?.conversation_initiation_client_data)?.dynamic_variables)
  return Object.entries(variables ?? {}).map(([key, value]) => [key, parseJsonString(value)])
}

export type CallOverview = {
  conversationId?: string
  terminationReason?: string
  callDurationSecs?: number
  errorCode?: string
  errorReason?: string
  callSuccessful?: string
  summaryTitle?: string
  summary?: string
}

export function readCallOverview(data: unknown): CallOverview {
  const webhook = webhookOf(data)
  const metadata = asRecord(webhook?.metadata)
  const error = asRecord(metadata?.error)
  const analysis = asRecord(webhook?.analysis)
  const text = (value: unknown) => (value === undefined || value === null || value === `` ? undefined : String(value))
  return {
    conversationId: text(webhook?.conversation_id),
    terminationReason: text(metadata?.termination_reason),
    callDurationSecs: typeof metadata?.call_duration_secs === `number` ? metadata.call_duration_secs : undefined,
    errorCode: text(error?.code),
    errorReason: text(error?.reason),
    callSuccessful: text(analysis?.call_successful),
    summaryTitle: text(analysis?.call_summary_title),
    summary: text(analysis?.transcript_summary),
  }
}

/**
 * ElevenLabs & GPT failure types for a call (plan D32, D33), from its termination
 * reason, error metadata and ElevenLabs' success verdict. Termination reasons are
 * prose, so they're matched loosely and case-insensitively.
 */
export function classifyCallFailures(overview: CallOverview): CallFailureType[] {
  const reason = overview.terminationReason?.toLowerCase() ?? ``
  const checks: [CallFailureType, boolean][] = [
    [`llmFailed`, reason.includes(`custom_llm generation failed`)],
    [`llmSlow`, reason.includes(`llm response took too long`)],
    [
      `disconnected`,
      reason.startsWith(`client disconnected`) || (reason.includes(`no user message`) && reason.includes(`network`)),
    ],
    [`maxDuration`, reason.includes(`maximum duration`)],
    [`error`, !!(overview.errorCode || overview.errorReason)],
    [`unsuccessful`, overview.callSuccessful === `failure`],
  ]
  return checks.filter(([, matches]) => matches).map(([type]) => type)
}

/** The `no_booking_reason` data-collection value (plan D35); undefined when the call has none. */
export function readNoBookingReason(data: unknown): string | undefined {
  const analysis = asRecord(webhookOf(data)?.analysis)
  const entry = asRecord(analysis?.data_collection_results)?.no_booking_reason
  const nested = asRecord(entry)
  const value = nested ? nested.value : entry
  return typeof value === `string` && value && value !== `null` ? value : undefined
}

export type AnalysisResult = { kind: `Evaluation` | `Data collection`; id: string; value: unknown; rationale?: string }

/** Evaluation criteria and data collection results other than the tool diagnostics. */
export function readAnalysisResults(data: unknown): AnalysisResult[] {
  const analysis = asRecord(webhookOf(data)?.analysis)
  const toolKeys = new Set(TOOL_DIAGNOSTIC_KEYS.map(([, key]) => key))
  const read = (kind: AnalysisResult[`kind`], group: unknown, valueKey: string): AnalysisResult[] =>
    Object.entries(asRecord(group) ?? {})
      .filter(([id]) => !toolKeys.has(id))
      .map(([id, raw]) => {
        const result = asRecord(raw)
        return {
          kind,
          id,
          value: result ? result[valueKey] : raw,
          rationale: typeof result?.rationale === `string` ? result.rationale : undefined,
        }
      })
  return [
    ...read(`Evaluation`, analysis?.evaluation_criteria_results, `result`),
    ...read(`Data collection`, analysis?.data_collection_results, `value`),
  ]
}
